/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { LatLng } from '../app/core/models';
import { GoogleMapsShape } from '../app/maps/models';

export class GoogleMapsShapeUtils {
  public static getRadiusFromBounds(bounds: google.maps.LatLngBounds): number {
    const center = bounds.getCenter();
    const northEast = bounds.getNorthEast();
    northEast.lat = center.lat;
    return google.maps.geometry.spherical.computeDistanceBetween(center, northEast);
  }

  public static getLatLngFromShape(shape: GoogleMapsShape): LatLng {
    const bounds = this.getBoundsFromShape(shape);
    return LatLng.fromGoogleMaps(bounds.getCenter());
  }

  public static getBoundsFromShape(shape: GoogleMapsShape): google.maps.LatLngBounds {
    if (shape instanceof google.maps.Polygon) {
      // Calculate polygon bounds manually
      const bounds = new google.maps.LatLngBounds();
      shape.getPaths().forEach(x => x.forEach(latlng => bounds.extend(latlng)));
      return bounds;
    }
    return shape.getBounds();
  }

  public static moveShape(shape: GoogleMapsShape, newCenter: LatLng): GoogleMapsShape {
    newCenter = new LatLng(newCenter.lat, newCenter.lng);
    const cloneShape = this.cloneShape(shape);
    if (cloneShape instanceof google.maps.Circle) {
      cloneShape.setCenter(newCenter);
      return cloneShape;
    }
    if (cloneShape instanceof google.maps.Rectangle) {
      return this._moveRectangle(cloneShape, newCenter);
    }
    if (cloneShape instanceof google.maps.Polygon) {
      return this._movePolygon(cloneShape, newCenter);
    }
  }

  public static cloneShape(shape: GoogleMapsShape, cloneMap = false): GoogleMapsShape {
    if (shape instanceof google.maps.Polygon) {
      return this._clonePolygon(shape, cloneMap);
    }
    if (shape instanceof google.maps.Rectangle) {
      return this._cloneRectangle(shape, cloneMap);
    }
    return this._cloneCircle(shape, cloneMap);
  }

  public static getBaseOptions(shape: GoogleMapsShape, cloneMap = false) {
    const baseOptions = {
      clickable: shape.get('clickable'),
      draggable: shape.get('draggable'),
      editable: shape.get('editable'),
      fillColor: shape.get('fillColor'),
      fillOpacity: shape.get('fillOpacity'),
      map: shape.get('map'),
      geodesic: shape.get('geodesic'),
      strokeColor: shape.get('strokeColor'),
      strokeOpacity: shape.get('strokeOpacity'),
      strokePosition: shape.get('strokePosition'),
      strokeWeight: shape.get('strokeWeight'),
      visible: shape.get('visible'),
      zIndex: shape.get('zIndex')
    };

    if (!cloneMap) {
      baseOptions.map = undefined;
    }

    return baseOptions;
  }

  private static _clonePolygon(polygon: google.maps.Polygon, cloneMap: boolean): google.maps.Polygon {
    const newPolygon = new google.maps.Polygon({
      ...this.getBaseOptions(polygon, cloneMap),
      paths: polygon.getPaths()
    });

    return newPolygon;
  }

  private static _cloneRectangle(rectangle: google.maps.Rectangle, cloneMap: boolean): google.maps.Rectangle {
    const newRectangle = new google.maps.Rectangle({
      ...this.getBaseOptions(rectangle, cloneMap),
      bounds: rectangle.getBounds()
    });

    return newRectangle;
  }

  private static _cloneCircle(circle: google.maps.Circle, cloneMap: boolean): google.maps.Circle {
    const newCircle = new google.maps.Circle({
      ...this.getBaseOptions(circle, cloneMap),
      center: circle.getCenter(),
      radius: circle.getRadius()
    });

    return newCircle;
  }

  private static _moveRectangle(rectangle: google.maps.Rectangle, newCenter: LatLng): GoogleMapsShape {
    const { distance, heading } = this._calculateShapeOffsetFromLatLng(rectangle, newCenter);
    const newNorthEast = google.maps.geometry.spherical.computeOffset(rectangle.getBounds().getNorthEast(), distance, heading);
    const newSouthWest = google.maps.geometry.spherical.computeOffset(rectangle.getBounds().getSouthWest(), distance, heading);
    const newBounds = new google.maps.LatLngBounds(newSouthWest, newNorthEast);
    rectangle.setBounds(newBounds);
    return rectangle;
  }

  private static _movePolygon(polygon: google.maps.Polygon, newCenter: LatLng): GoogleMapsShape {
    // This assumes the shape is geodesic
    const { distance, heading } = this._calculateShapeOffsetFromLatLng(polygon, newCenter);
    const paths = polygon.getPaths();
    const newPaths = paths
      .getArray()
      .map(path => path.getArray().map(point => google.maps.geometry.spherical.computeOffset(point, distance, heading)));
    polygon.setPaths(newPaths);
    return polygon;
  }

  private static _calculateShapeOffsetFromLatLng(shape: GoogleMapsShape, newCenter: LatLng): { distance: number; heading: number } {
    const currentCenter = this.getLatLngFromShape(shape);
    const heading = google.maps.geometry.spherical.computeHeading(currentCenter.toGoogleMaps(), newCenter.toGoogleMaps());
    const distance = google.maps.geometry.spherical.computeDistanceBetween(currentCenter.toGoogleMaps(), newCenter.toGoogleMaps());
    return { heading, distance };
  }
}
